import { useState, useCallback } from 'react';

/**
 * Hook to track user progress across lessons
 * Stores data in localStorage under 'pilotpath_progress'
 */
export function useProgressTracker() {
  const [progress, setProgress] = useState(() => {
    try {
      const stored = localStorage.getItem('pilotpath_progress');
      return stored ? JSON.parse(stored) : {
        completedLessons: [], // Array of "{topic}::{subtopic}"
        quizScores: [],       // Array of { topic, subtopic, score, date }
        lastActivity: null,   // Date of last activity
        studyStreak: 0,
        totalStudyMinutes: 0,
      };
    } catch {
      return {
        completedLessons: [],
        quizScores: [],
        lastActivity: null,
        studyStreak: 0,
        totalStudyMinutes: 0,
      };
    }
  });

  const saveProgress = useCallback((newProgress) => {
    setProgress(newProgress);
    localStorage.setItem('pilotpath_progress', JSON.stringify(newProgress));
  }, []);

  // Mark a lesson as completed
  const completeLesson = useCallback((topic, subtopic) => {
    const lessonKey = `${topic}::${subtopic}`;
    setProgress(prev => {
      if (prev.completedLessons.includes(lessonKey)) {
        return prev; // Already completed
      }

      const updatedProgress = {
        ...prev,
        completedLessons: [...prev.completedLessons, lessonKey],
        lastActivity: new Date().toISOString(),
      };
      saveProgress(updatedProgress);
      return updatedProgress;
    });
  }, [saveProgress]);

  // Record a quiz score
  const recordQuizScore = useCallback((topic, subtopic, score) => {
    setProgress(prev => {
      const updatedProgress = {
        ...prev,
        quizScores: [
          ...prev.quizScores,
          {
            topic,
            subtopic,
            score,
            date: new Date().toISOString(),
          }
        ],
        lastActivity: new Date().toISOString(),
      };
      saveProgress(updatedProgress);
      return updatedProgress;
    });
  }, [saveProgress]);

  // Add study minutes
  const addStudyTime = useCallback((minutes) => {
    setProgress(prev => {
      const updatedProgress = {
        ...prev,
        totalStudyMinutes: prev.totalStudyMinutes + minutes,
        lastActivity: new Date().toISOString(),
      };
      saveProgress(updatedProgress);
      return updatedProgress;
    });
  }, [saveProgress]);

  // Calculate statistics
  const getStats = useCallback(() => {
    const totalLessons = progress.completedLessons.length;
    
    // Average quiz score
    const avgQuizScore = progress.quizScores.length > 0
      ? Math.round(progress.quizScores.reduce((sum, q) => sum + q.score, 0) / progress.quizScores.length)
      : 0;

    // Recent quizzes (last 5)
    const recentQuizzes = progress.quizScores
      .sort((a, b) => new Date(b.date) - new Date(a.date))
      .slice(0, 5)
      .map(q => ({
        ...q,
        label: q.subtopic.split('—')[0].trim(),
      }));

    // Low scoring topics
    const topicScores = {};
    progress.quizScores.forEach(q => {
      if (!topicScores[q.topic]) {
        topicScores[q.topic] = { scores: [], subtopics: [] };
      }
      topicScores[q.topic].scores.push(q.score);
      topicScores[q.topic].subtopics.push(q.subtopic);
    });

    const weakAreas = Object.entries(topicScores)
      .map(([topic, data]) => ({
        topic,
        avgScore: Math.round(data.scores.reduce((a, b) => a + b, 0) / data.scores.length),
        subtopics: data.subtopics,
      }))
      .filter(t => t.avgScore < 75)
      .sort((a, b) => a.avgScore - b.avgScore)
      .slice(0, 3);

    // Study streak (days in a row)
    const studyStreak = calculateStreak(progress.quizScores);

    // Completion percentage
    const completionPct = 0; // Will be calculated based on all available lessons

    return {
      totalLessons,
      avgQuizScore,
      recentQuizzes,
      weakAreas,
      studyStreak,
      studyHours: (progress.totalStudyMinutes / 60).toFixed(1),
      completionPct,
    };
  }, [progress]);

  // Calculate study streak
  const calculateStreak = (quizzes) => {
    if (quizzes.length === 0) return 0;

    const sortedDates = quizzes
      .map(q => new Date(q.date).toDateString())
      .filter((date, idx, arr) => idx === 0 || arr[idx - 1] !== date); // Unique days

    let streak = 1;
    for (let i = 1; i < sortedDates.length; i++) {
      const prevDate = new Date(sortedDates[i - 1]);
      const currDate = new Date(sortedDates[i]);
      const dayDiff = (prevDate - currDate) / (1000 * 60 * 60 * 24);

      if (dayDiff === 1) {
        streak++;
      } else {
        break;
      }
    }
    return streak;
  };

  return {
    progress,
    completeLesson,
    recordQuizScore,
    addStudyTime,
    getStats,
  };
}
